import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { resolveTenantCode, toTenantPath } from 'src/lib/tenantRouting';

interface BaseLinkProps {
  children: React.ReactNode;
  className?: string;
  variant?: 'default';
  'aria-label'?: string;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
}

interface InternalLinkProps extends BaseLinkProps {
  to: string;
  href?: never;
}

interface ExternalLinkProps extends BaseLinkProps {
  href: string;
  to?: never;
  target?: '_blank' | '_self' | '_parent' | '_top';
  rel?: string;
}

type LinkProps = InternalLinkProps | ExternalLinkProps;

const Link: React.FC<LinkProps> = ({ children, className = '', variant = 'default', ...props }) => {
  const location = useLocation();
  const baseStyles = 'transition-colors duration-200 hover:underline';

  const variantStyles = {
    default: 'text-misc-link hover:underline'
  };

  // Check if it's an external link
  const isExternal = 'href' in props;

  if (isExternal) {
    const { href, target, rel, ...rest } = props as ExternalLinkProps;

    // Determine if it's a special link type (mailto or tel)
    const isSpecialLink = href.startsWith('mailto:') || href.startsWith('tel:');

    // For special links, we don't want target="_blank" or rel attributes
    const linkProps = isSpecialLink
      ? { href }
      : {
          href,
          target: target || '_blank',
          rel: rel || 'noopener noreferrer'
        };

    return (
      <a {...linkProps} className={twMerge(baseStyles, variantStyles[variant], className)} {...rest}>
        {children}
      </a>
    );
  }

  // Internal link using React Router
  const { to, ...rest } = props as InternalLinkProps;
  const tenantCode = resolveTenantCode(location.pathname);
  return (
    <RouterLink
      to={toTenantPath(to, tenantCode)}
      className={twMerge(baseStyles, variantStyles[variant], className)}
      {...rest}
    >
      {children}
    </RouterLink>
  );
};

export default Link;
